/**
 * 深邃蓝模板 - 共享导航栏
 * 正确显示后台配置的导航菜单和分类
 */

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSiteConfig } from "@/context/SiteConfigContext";

export default function DeepBlueNavbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { siteConfig, navMenus, navCategories, loading: configLoading } = useSiteConfig();
  const { isLoggedIn } = useAuth();

  const siteName = siteConfig?.site_name || siteConfig?.name || "";
  const logo = siteConfig?.logo || "";
  const enableVip = siteConfig?.enable_vip !== false;

  return (
    <nav className="sticky top-0 z-40 bg-[#0F1D2F] shadow-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        {/* Logo + 站点名 */}
        <Link to="/" className="flex items-center gap-2.5 shrink-0">
          {logo ? (
            <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center p-1 overflow-hidden">
              <img src={logo} alt={siteName} className="w-full h-full object-contain brightness-0 invert" />
            </div>
          ) : siteName ? (
            <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center text-white font-bold text-sm">
              {siteName.charAt(0)}
            </div>
          ) : null}
          {siteName && <span className="text-lg font-bold text-white">{siteName}</span>}
        </Link>

        {/* 中间：导航菜单 */}
        <div className="hidden lg:flex items-center gap-0.5">
          <Link to="/" className="px-3 py-1.5 text-sm font-medium text-blue-200 hover:text-white rounded-md transition-colors">
            首页
          </Link>
          {navMenus.map((menu) => (
            <Link
              key={menu.id}
              to={menu.type === 'category' ? `/category?cat=${menu.target_id || ''}` :
                   menu.type === 'page' ? `/page/${menu.target_url || ''}` :
                   menu.type === 'link' ? (menu.target_url || '#') : '#'}
              className="px-3 py-1.5 text-sm font-medium text-blue-200 hover:text-white rounded-md transition-colors"
            >
              {menu.name}
            </Link>
          ))}
          {navCategories.map((cat) => (
            <Link
              key={cat.id}
              to={`/category?cat=${cat.slug}`}
              className="px-3 py-1.5 text-sm font-medium text-blue-200 hover:text-white rounded-md transition-colors"
            >
              {cat.name}
            </Link>
          ))}
          {enableVip && (
            <Link to="/vip" className="px-3 py-1.5 text-sm font-medium text-amber-300 hover:text-amber-200 rounded-md transition-colors">
              <i className="ri-vip-crown-line mr-1" />VIP
            </Link>
          )}
        </div>

        {/* 右侧 */}
        <div className="flex items-center gap-3">
          {/* 发布按钮 */}
          <button
            onClick={() => navigate("/publish")}
            className="inline-flex items-center gap-1 px-3.5 py-2 text-sm font-bold bg-blue-600 text-white rounded-md hover:bg-blue-500 transition-colors shadow-sm"
          >
            <i className="ri-add-line text-base" />
            <span className="hidden sm:inline">立即发布</span>
          </button>

          {isLoggedIn ? (
            <Link to="/dashboard" className="text-sm text-blue-200 hover:text-white">会员中心</Link>
          ) : (
            <>
              <Link to="/login" className="text-sm text-blue-200 hover:text-white">登录</Link>
              <Link to="/register" className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors">注册</Link>
            </>
          )}

          {/* 移动端菜单按钮 */}
          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden p-1.5 rounded-lg text-blue-200 hover:text-white">
            <i className={`ri-${isMobileMenuOpen ? "close" : "menu"}-line text-xl`} />
          </button>
        </div>
      </div>

      {/* 移动端菜单 */}
      {isMobileMenuOpen && (
        <div className="lg:hidden border-t border-blue-800/50 bg-[#122236]">
          <div className="px-4 py-2 space-y-0.5 max-h-[60vh] overflow-y-auto">
            <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-blue-200 hover:text-white rounded-lg transition-colors">
              <i className="ri-home-4-line mr-2" />首页
            </Link>
            {navMenus.map((menu) => (
              <Link
                key={menu.id}
                to={menu.type === 'category' ? `/category?cat=${menu.target_id || ''}` :
                     menu.type === 'page' ? `/page/${menu.target_url || ''}` :
                     menu.type === 'link' ? (menu.target_url || '#') : '#'}
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-3 py-2.5 text-sm font-medium text-blue-200 hover:text-white rounded-lg transition-colors"
              >
                <i className="ri-menu-line mr-2" />{menu.name}
              </Link>
            ))}
            {navCategories.map((cat) => (
              <Link key={cat.id} to={`/category?cat=${cat.slug}`} onClick={() => setIsMobileMenuOpen(false)}
                className="block px-3 py-2.5 text-sm font-medium text-blue-200 hover:text-white rounded-lg transition-colors">
                <i className="ri-grid-line mr-2" />{cat.name}
              </Link>
            ))}
            {enableVip && (
              <Link to="/vip" onClick={() => setIsMobileMenuOpen(false)}
                className="block px-3 py-2.5 text-sm font-medium text-amber-300 hover:text-white rounded-lg transition-colors">
                <i className="ri-vip-crown-line mr-2" />VIP会员
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
